import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AlertService } from './alert.service';
import { AlertState, AlertType } from '../../../shared/models/design-system.types';

/**
 * Alert Container Component
 *
 * Renders toast notifications managed by AlertService.
 * Place this component once in your app root.
 *
 * Usage in app.component.html:
 * ```html
 * <hc-alert-container></hc-alert-container>
 * ```
 */
@Component({
    selector: 'hc-alert-container',
    templateUrl: './alert.component.html',
    styleUrls: ['./alert.component.scss'],
    standalone: false
})
export class AlertContainerComponent implements OnInit, OnDestroy {
  alerts: AlertState[] = [];
  private destroy$ = new Subject<void>();

  constructor(private alertService: AlertService) {}

  ngOnInit(): void {
    this.alertService.alerts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(alerts => {
        this.alerts = alerts;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  dismiss(id: string): void {
    this.alertService.dismiss(id);
  }

  getIcon(type: AlertType): string {
    switch (type) {
      case 'success':
        return 'circle-check';
      case 'danger':
        return 'circle-alert';
      case 'warning':
        return 'triangle-alert';
      case 'info':
        return 'info';
      default:
        return 'info';
    }
  }

  onActionClick(alert: AlertState): void {
    if (alert.action?.callback) {
      alert.action.callback();
    }
    this.dismiss(alert.id);
  }

  trackById(index: number, alert: AlertState): string {
    return alert.id;
  }
}
